import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "../icon";
import { ButtonSpinner } from "../spinner";

const buttonVariants = cva(
  [
    "inline-flex",
    "items-center",
    "justify-center",
    "whitespace-nowrap",
    "shrink-0",
    "rounded-(--radius-sm)",
    "px-(--spacing-12)",
    "border",
    "border-solid",
    "font-(family-name:--font-family-label)",
    "font-(number:--font-weight-medium)",
    "cursor-pointer",
    "select-none",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "outline-none",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-button-primary-default-background",
          "text-button-primary-default-text",
          "border-button-primary-default-background",
          "hover:bg-button-primary-hover-background",
          "hover:border-button-primary-hover-background",
          "active:bg-button-primary-pressed-background",
          "active:border-button-primary-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        outline: [
          "bg-button-outline-default-background",
          "text-button-outline-default-text",
          "border-button-outline-default-border",
          "hover:bg-button-outline-hover-background",
          "hover:border-button-outline-hover-border",
          "active:bg-button-outline-pressed-background",
          "active:border-button-outline-pressed-border",
          "data-[mouse-down]:bg-button-outline-pressed-background",
          "data-[mouse-down]:border-button-outline-pressed-border",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        ghost: [
          "bg-transparent",
          "text-button-ghost-default-text",
          "border-transparent",
          "hover:bg-button-ghost-hover-background",
          "active:bg-button-ghost-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        info: [
          "bg-button-info-default-background",
          "text-button-info-default-text",
          "border-button-info-default-background",
          "hover:bg-button-info-hover-background",
          "hover:border-button-info-hover-background",
          "active:bg-button-info-pressed-background",
          "active:border-button-info-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
      },
      intent: {
        default: null,
        destructive: null,
      },
      size: {
        sm: [
          "h-(--spacing-32)",
          "gap-(--spacing-8)",
          "text-(length:--font-size-body-medium)",
          "leading-(--font-line-height-20)",
        ],
        md: [
          "h-(--spacing-40)",
          "gap-(--spacing-8)",
          "text-(length:--font-size-body-large)",
          "leading-(--font-line-height-24)",
        ],
        lg: [
          "h-[44px]",
          "gap-(--spacing-8)",
          "px-(--spacing-16)",
          "text-(length:--font-size-body-large)",
          "leading-(--font-line-height-24)",
        ],
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        intent: "destructive",
        class: [
          "bg-button-primary-destructive-default-background",
          "text-button-primary-destructive-text",
          "border-button-primary-destructive-default-background",
          "hover:bg-button-primary-destructive-hover-background",
          "hover:border-button-primary-destructive-hover-background",
          "active:bg-button-primary-destructive-pressed-background",
          "active:border-button-primary-destructive-pressed-background",
        ],
      },
      {
        variant: "ghost",
        intent: "destructive",
        class: ["text-text-destructive"],
      },
    ],
    defaultVariants: {
      variant: "primary",
      intent: "default",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends Omit<ButtonPrimitive.Props, "className">,
    VariantProps<typeof buttonVariants> {
  /** Visible button label */
  children: string;
  /** Optional icon displayed after the label */
  icon?: IconName;
  /** Loading state — shows spinner, disables interaction */
  loading?: boolean;
  /** Text shown during loading. "..." is appended automatically. Defaults to "Loading". */
  loadingText?: string;
  /** Stretch to full width of container */
  fullWidth?: boolean;
}

/**
 * Primary action button with multiple visual styles and interaction states.
 *
 * @example
 * <Button variant="primary">Save</Button>
 * <Button variant="ghost" intent="destructive">Delete</Button>
 * <Button loading loadingText="Saving">Save</Button>
 */
function Button({
  children,
  variant,
  intent,
  size,
  icon,
  loading = false,
  loadingText = "Loading",
  fullWidth = false,
  disabled = false,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <ButtonPrimitive
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(
        buttonVariants({ variant, intent, size }),
        fullWidth && "w-full",
        isDisabled && "opacity-40 pointer-events-none",
        disabled && "cursor-not-allowed",
        loading && !disabled && "cursor-default",
      )}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      tabIndex={isDisabled ? -1 : undefined}
      {...props}
    >
      {loading ? `${loadingText}...` : children}
      {loading ? (
        <ButtonSpinner size="sm" />
      ) : icon ? (
        <Icon name={icon} size="sm" tone="inherit" />
      ) : null}
    </ButtonPrimitive>
  );
}

export { Button };
